import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList, asRows, fetchAll, pageTotal } from '@/lib/api/list'
import {
  catalogFromGroups,
  catalogFromPermissions,
  emptyPermissionCatalog,
  type ApiPermission,
  type PermissionCatalog,
} from './permissions'
import { fetchEmployeeDirectory, hrKeys } from '@/features/hr/personel/api'
import {
  POSITION_LEVELS,
  ROLE_SCOPES,
  type AccessProject,
  type AccessUser,
  type AssignedPerson,
  type Department,
  type AffectedEmployee,
  type AffectedRoleUser,
  type DepartmentInput,
  type EmployeeReassignmentWarning,
  type DepartmentManager,
  type DepartmentRef,
  type DirectoryUserInput,
  type DirectoryUserRow,
  type LinkableEmployee,
  type MailAccountInput,
  type MailboxInput,
  type MailboxUpdateInput,
  type MailProviderOption,
  type UserMailbox,
  type Position,
  type PositionInput,
  type PositionLevel,
  type RecordStatus,
  type Role,
  type RoleActivityEntry,
  type RoleActivityPage,
  type RoleActor,
  type RoleHistoryEntry,
  type RoleHistoryKind,
  type RoleInput,
  type RolePermission,
  type RoleScope,
  type UserRoleReassignmentWarning,
} from './types'

const keys = {
  permissions: ['access', 'permissions'] as const,
  roles: ['access', 'roles'] as const,
  role: (id: string) => ['access', 'roles', id] as const,
  roleActivity: (id: string, page: number, action: string) => ['access', 'roles', id, 'activity', page, action] as const,
  users: ['access', 'users'] as const,
  projects: ['access', 'projects'] as const,
  departments: ['access', 'departments'] as const,
  positions: ['access', 'positions'] as const,
  mailProviders: ['access', 'mail', 'providers'] as const,
  user: (id: string) => ['access', 'users', id] as const,
}

type DirectoryUser = AccessUser & { roleIds: string[] }

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function str(v: unknown, fallback = ''): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return fallback
}

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v)
  return fallback
}

function strList(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.map((item) => (typeof item === 'string' ? item : str(rec(item).id || rec(item).code))).filter(Boolean)
}

function statusOf(raw: Record<string, unknown>): RecordStatus {
  const status = str(raw.status).toLowerCase()
  if (status === 'passive' || status === 'inactive' || status === 'disabled') return 'passive'
  if (raw.is_active === false || raw.active === false) return 'passive'
  return 'active'
}

function scopeOf(raw: Record<string, unknown>): RoleScope {
  const scope = str(raw.scope)
  return (ROLE_SCOPES as readonly string[]).includes(scope) ? (scope as RoleScope) : 'organization'
}

const HISTORY: readonly RoleHistoryKind[] = ['created', 'updated', 'permissions', 'users', 'status']

function mapHistory(raw: unknown): RoleHistoryEntry[] {
  if (!Array.isArray(raw)) return []
  return raw.map((item, index) => {
    const row = rec(item)
    const kind = str(row.kind)
    const status = row.status === 'active' || row.status === 'passive' ? row.status : undefined
    return {
      id: str(row.id, `h-${index}`),
      at: str(row.at ?? row.created_at ?? row.updated_at),
      by: str(row.by ?? row.actor ?? row.updated_by),
      kind: (HISTORY as readonly string[]).includes(kind) ? (kind as RoleHistoryKind) : 'updated',
      added: typeof row.added === 'number' ? row.added : undefined,
      removed: typeof row.removed === 'number' ? row.removed : undefined,
      status,
    }
  })
}

function mapRolePermissionItems(raw: Record<string, unknown>, catalog: PermissionCatalog): RolePermission[] {
  const direct = raw.permissions ?? raw.permission_codes ?? raw.codes
  if (Array.isArray(direct) && direct.length) {
    return direct
      .map((item) => {
        if (typeof item === 'string') {
          const asCode = catalog.idByCode[item] ? item : catalog.codeById[item]
          const code = asCode || item
          const id = catalog.idByCode[code] || (catalog.codeById[item] ? item : '')
          return {
            id,
            code,
            description: catalog.descriptionByCode[code] || '',
            module: '',
          } satisfies RolePermission
        }
        const row = rec(item)
        const id = str(row.id)
        const code = str(row.code) || catalog.codeById[id] || ''
        if (!code) return null
        return {
          id: id || catalog.idByCode[code] || '',
          code,
          description: str(row.description) || catalog.descriptionByCode[code] || '',
          module: str(row.module),
        } satisfies RolePermission
      })
      .filter((p): p is RolePermission => !!p && !!p.code)
  }
  return strList(raw.permission_ids).map((id) => {
    const code = catalog.codeById[id] ?? id
    return {
      id,
      code,
      description: catalog.descriptionByCode[code] || '',
      module: '',
    }
  })
}

function permissionCodes(raw: Record<string, unknown>, catalog: PermissionCatalog): string[] {
  return [...new Set(mapRolePermissionItems(raw, catalog).map((p) => p.code))]
}

function mapEmployeeDetails(raw: unknown): AccessUser {
  const row = rec(raw)
  const department = rec(row.department)
  const position = rec(row.position)
  const first = str(row.first_name ?? row.firstName)
  const last = str(row.last_name ?? row.lastName)
  const email = str(row.work_email ?? row.email)
  return {
    id: str(row.id),
    name: str(row.name) || [first, last].filter(Boolean).join(' ') || email,
    email,
    departmentId: str(row.department_id ?? row.departmentId ?? department.id),
    department: str(department.name ?? row.department_name ?? row.departmentName),
    position: str(position.title ?? row.position_title ?? row.job_title ?? row.title),
    avatarUrl: str(row.photo_url ?? row.avatar_url ?? row.avatar) || undefined,
  }
}

function mapLinkableEmployee(raw: unknown): LinkableEmployee {
  const row = rec(raw)
  const base = mapEmployeeDetails(raw)
  const workEmail = str(row.work_email ?? row.email) || base.email
  const status = str(row.status ?? row.employment_status ?? row.durum).toLowerCase()
  const isActive =
    row.is_active !== false &&
    status !== 'terminated' &&
    status !== 'inactive' &&
    status !== 'pasif' &&
    status !== 'archived'
  const userId = str(row.user_id ?? row.userId) || null
  const hasUser = Boolean(row.has_system_access ?? userId)
  return { ...base, email: workEmail || base.email, workEmail, isActive, hasUser, userId }
}

function mailboxBody(mailbox: MailboxInput) {
  return {
    provider_id: mailbox.providerId,
    secret: mailbox.secret,
    ...(mailbox.email?.trim() ? { email: mailbox.email.trim() } : {}),
    ...(mailbox.signatureHtml?.trim() ? { signature_html: mailbox.signatureHtml.trim() } : {}),
  }
}

function mapUserMailbox(raw: unknown): UserMailbox | null {
  if (raw == null) return null
  const row = rec(raw)
  const id = str(row.id)
  if (!id && !str(row.provider_id ?? row.providerId)) return null
  return {
    id,
    providerId: str(row.provider_id ?? row.providerId),
    providerName: str(row.provider_name ?? row.providerName),
    providerKind: str(row.provider_kind ?? row.providerKind ?? row.kind),
    email: str(row.email),
    signatureHtml: str(row.signature_html ?? row.signatureHtml),
    hasSecret: Boolean(row.has_secret ?? row.hasSecret),
    isActive: row.is_active !== false && row.isActive !== false,
  }
}

function mailboxUpdateBody(mailbox: MailboxUpdateInput): Record<string, unknown> {
  if ('remove' in mailbox && mailbox.remove) return { remove: true }
  const body: Record<string, unknown> = {}
  if (mailbox.providerId) body.provider_id = mailbox.providerId
  if (mailbox.email !== undefined) body.email = mailbox.email.trim()
  if (mailbox.secret?.trim()) body.secret = mailbox.secret.trim()
  if (mailbox.signatureHtml !== undefined) body.signature_html = mailbox.signatureHtml
  return body
}

function mapUser(raw: unknown): DirectoryUser {
  const row = rec(raw)
  return {
    ...mapEmployeeDetails(raw),
    roleIds: strList(row.role_ids ?? row.roles),
  }
}

/** BE rol cevabı `user_ids` taşır; kullanıcı dizinini çekmeden atamalar çıkarılır. */
function assignmentsFrom(raw: Record<string, unknown>) {
  return strList(raw.user_ids ?? raw.userIds).map((userId) => ({ userId, assignedAt: '', assignedBy: '' }))
}

function mapRoleActor(raw: unknown): RoleActor | null {
  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return null
    return { id: '', email: text.includes('@') ? text : '', fullName: text }
  }
  const row = rec(raw)
  const id = str(row.id)
  const email = str(row.email)
  const fullName = str(row.full_name ?? row.fullName) || email
  if (!id && !email && !fullName) return null
  return { id, email, fullName }
}

function mapRole(raw: unknown, catalog: PermissionCatalog): Role {
  const row = rec(raw)
  const id = str(row.id)
  const assignments = assignmentsFrom(row)
  const userCount = num(row.user_count ?? row.userCount, assignments.length)
  return {
    id,
    name: str(row.name),
    description: str(row.description),
    scope: scopeOf(row),
    scopeTargets: strList(row.scope_targets ?? row.scopeTargets),
    status: statusOf(row),
    isSystem: row.is_system === true || row.isSystem === true,
    permissionItems: mapRolePermissionItems(row, catalog),
    permissions: permissionCodes(row, catalog),
    assignments,
    userCount,
    version: num(row.version ?? row.permission_version ?? row.permissionVersion, 1),
    createdAt: str(row.created_at ?? row.createdAt),
    updatedAt: str(row.updated_at ?? row.updatedAt),
    createdBy: mapRoleActor(row.created_by ?? row.createdBy),
    updatedBy: mapRoleActor(row.updated_by ?? row.updatedBy),
    history: mapHistory(row.history),
  }
}

function mapRoleActivityEntry(raw: unknown): RoleActivityEntry | null {
  const row = rec(raw)
  const id = str(row.id)
  if (!id) return null
  const actor = mapRoleActor(row.actor)
  const payload = rec(row.payload)
  return {
    id,
    action: str(row.action),
    actionLabel: str(row.action_label ?? row.actionLabel),
    actor,
    actorId: actor?.id ?? '',
    actorEmail: actor?.email ?? '',
    ipAddress: str(row.ip_address ?? row.ipAddress),
    createdAt: str(row.created_at ?? row.createdAt),
    summary: str(row.summary),
    payload,
  }
}

function mapAffectedRoleUser(raw: unknown): AffectedRoleUser | null {
  const row = rec(raw)
  const id = str(row.id)
  if (!id) return null
  return {
    id,
    email: str(row.email),
    fullName: str(row.full_name ?? row.fullName) || str(row.email),
    currentRoleIds: strList(row.current_role_ids ?? row.currentRoleIds),
    currentRoleNames: strList(row.current_role_names ?? row.currentRoleNames),
  }
}

export function parseUserRoleReassignmentWarning(raw: unknown): UserRoleReassignmentWarning | null {
  const row = rec(raw)
  if (!row.operation && !row.affected_users && !row.affectedUsers) return null
  const affectedRaw = Array.isArray(row.affected_users)
    ? row.affected_users
    : Array.isArray(row.affectedUsers)
      ? row.affectedUsers
      : []
  const affectedUsers = affectedRaw.map(mapAffectedRoleUser).filter((u): u is AffectedRoleUser => !!u)
  if (!affectedUsers.length) return null
  return {
    operation: str(row.operation),
    message: str(row.message),
    affectedCount: num(row.affected_count ?? row.affectedCount, affectedUsers.length),
    affectedUsers,
  }
}

function mapDepartmentRef(raw: unknown): DepartmentRef | null {
  const row = rec(raw)
  const id = str(row.id)
  if (!id) return null
  return { id, name: str(row.name), code: str(row.code) }
}

function mapDepartmentManager(raw: unknown): DepartmentManager | null {
  const row = rec(raw)
  const id = str(row.id)
  if (!id) return null
  const firstName = str(row.first_name ?? row.firstName)
  const lastName = str(row.last_name ?? row.lastName)
  const name = str(row.name) || [firstName, lastName].filter(Boolean).join(' ') || str(row.email)
  const avatarUrl = str(row.photo_url ?? row.avatar_url ?? row.avatar) || undefined
  return { id, firstName, lastName, email: str(row.email), name, avatarUrl }
}

function mapDepartment(raw: unknown): Department {
  const row = rec(raw)
  const manager = mapDepartmentManager(row.manager)
  const parent = mapDepartmentRef(row.parent)
  const parentId = str(row.parent_id ?? row.parentId ?? parent?.id) || null
  const assignedRaw = Array.isArray(row.assigned_employees) ? row.assigned_employees : Array.isArray(row.assignedEmployees) ? row.assignedEmployees : []
  const members = assignedRaw.map(mapAssignedPerson).filter((p): p is AssignedPerson => !!p)
  return {
    id: str(row.id),
    name: str(row.name),
    code: str(row.code),
    description: str(row.description),
    managerId: str(row.manager_id ?? row.managerId ?? manager?.id) || null,
    parentId,
    parent,
    manager,
    members,
    memberCount: members.length,
    isActive: statusOf(row) === 'active',
    status: statusOf(row),
    createdAt: str(row.created_at ?? row.createdAt),
  }
}

function mapAssignedPerson(raw: unknown): AssignedPerson | null {
  const row = rec(raw)
  const id = str(row.id)
  if (!id) return null
  const firstName = str(row.first_name ?? row.firstName)
  const lastName = str(row.last_name ?? row.lastName)
  const name = str(row.name) || [firstName, lastName].filter(Boolean).join(' ') || str(row.email)
  const avatarUrl = str(row.photo_url ?? row.avatar_url ?? row.avatar) || undefined
  const position = str(row.position ?? row.title ?? row.job_title ?? rec(row.position).title ?? rec(row.position).name) || undefined
  return { id, firstName, lastName, name, email: str(row.email ?? row.work_email), position, avatarUrl }
}

function levelOf(raw: Record<string, unknown>): PositionLevel {
  const level = str(raw.level).toUpperCase()
  return (POSITION_LEVELS as readonly string[]).includes(level) ? (level as PositionLevel) : 'MID'
}

function mapPosition(raw: unknown): Position {
  const row = rec(raw)
  const departmentRaw = row.department
  const departmentRow = rec(departmentRaw)
  const departmentRef = mapDepartmentRef(departmentRaw)
  const assigned = Array.isArray(row.assigned_employees)
    ? row.assigned_employees
    : Array.isArray(row.assignedEmployees)
      ? row.assignedEmployees
      : []
  return {
    id: str(row.id),
    title: str(row.title ?? row.name),
    code: str(row.code),
    description: str(row.description),
    level: levelOf(row),
    isActive: !(row.is_active === false || row.active === false),
    departmentId: str(row.department_id ?? row.departmentId ?? departmentRef?.id),
    department: departmentRef
      ? {
          ...departmentRef,
          manager: mapDepartmentManager(departmentRow.manager),
          parent: mapDepartmentRef(departmentRow.parent),
        }
      : null,
    employees: assigned.map(mapAssignedPerson).filter((p): p is AssignedPerson => !!p),
    createdAt: str(row.created_at ?? row.createdAt),
  }
}

function mapDirectoryUser(raw: unknown): DirectoryUserRow {
  const row = rec(raw)
  const firstName = str(row.first_name ?? row.firstName)
  const lastName = str(row.last_name ?? row.lastName)
  const name = str(row.name) || [firstName, lastName].filter(Boolean).join(' ') || str(row.email)
  const employee = rec(row.employee)
  const roles = Array.isArray(row.roles) ? row.roles : []
  const roleIds = strList(row.role_ids ?? roles)
  const roleNames = roles
    .map((item) => (typeof item === 'string' ? '' : str(rec(item).name)))
    .filter(Boolean)
  return {
    id: str(row.id),
    name,
    firstName,
    lastName,
    email: str(row.email),
    isActive: statusOf(row) === 'active',
    roleIds,
    roleNames,
    employeeId: str(row.employee_id ?? row.employeeId ?? employee.id) || null,
    avatarUrl: str(row.avatar_url ?? row.photo_url ?? row.avatar ?? employee.photo_url ?? employee.avatar_url) || undefined,
    departmentName: str(row.department_name ?? rec(row.department).name ?? employee.department_name ?? rec(employee.department).name),
    position: str(row.position_title ?? row.position ?? row.title ?? employee.position ?? rec(employee.position).title ?? rec(employee.position).name),
    mailbox: mapUserMailbox(row.mailbox),
  }
}

function parseDepartments(data: unknown): Department[] {
  return asRows(data).map(mapDepartment)
}

function parsePositions(data: unknown): Position[] {
  return asRows(data).map(mapPosition)
}

function departmentBody(input: DepartmentInput) {
  const body: Record<string, unknown> = {
    name: input.name.trim(),
    description: input.description.trim(),
    manager_id: input.managerId || null,
    parent_id: input.parentId,
    is_active: input.isActive,
    confirm_employee_reassignment: Boolean(input.confirmEmployeeReassignment),
  }
  if (input.memberIds !== undefined) {
    body.assigned_employee_ids = [...new Set(input.memberIds.filter(Boolean))]
  }
  return body
}

function mapAffectedEmployee(raw: unknown): AffectedEmployee | null {
  const row = rec(raw)
  const id = str(row.id)
  if (!id) return null
  const firstName = str(row.first_name ?? row.firstName)
  const lastName = str(row.last_name ?? row.lastName)
  const name = str(row.name) || [firstName, lastName].filter(Boolean).join(' ') || str(row.email)
  const reasons = Array.isArray(row.reasons) ? row.reasons.map((r) => str(r)).filter(Boolean) : []
  return {
    id,
    firstName,
    lastName,
    name,
    email: str(row.email),
    currentDepartmentId: str(row.current_department_id ?? row.currentDepartmentId),
    currentDepartmentName: str(row.current_department_name ?? row.currentDepartmentName),
    currentPositionId: str(row.current_position_id ?? row.currentPositionId),
    currentPositionTitle: str(row.current_position_title ?? row.currentPositionTitle),
    currentPositionDepartmentId: str(row.current_position_department_id ?? row.currentPositionDepartmentId),
    reasons,
    willClearPosition: row.will_clear_position === true || row.willClearPosition === true,
  }
}

export function parseEmployeeReassignmentWarning(raw: unknown): EmployeeReassignmentWarning | null {
  const row = rec(raw)
  if (!row.operation && !row.affected_employees && !row.affectedEmployees) return null
  const affectedRaw = Array.isArray(row.affected_employees)
    ? row.affected_employees
    : Array.isArray(row.affectedEmployees)
      ? row.affectedEmployees
      : []
  const affectedEmployees = affectedRaw.map(mapAffectedEmployee).filter((e): e is AffectedEmployee => !!e)
  if (!affectedEmployees.length) return null
  return {
    operation: str(row.operation),
    message: str(row.message),
    affectedCount: num(row.affected_count ?? row.affectedCount, affectedEmployees.length),
    affectedEmployees,
  }
}

function rows(data: unknown) {
  return asList(data as unknown[] | { items?: unknown[] } | null)
}

function toPermissionIds(codes: string[], catalog: PermissionCatalog) {
  return codes.map((code) => catalog.idByCode[code] ?? code)
}

/** Kullanıcı dizini tek kaynaktır: `/users?all=true` bir kez çekilir, ekranlar `select` ile kendi modeline çevirir. */
const fetchUserRows = () => fetchAll('/api/v1/users')
const fetchRoleRows = () => fetchAll('/api/v1/roles')

export function usePermissionCatalog() {
  return useQuery({
    queryKey: keys.permissions,
    queryFn: async () => {
      const data = await api.get<unknown>('/api/v1/permissions')
      const envelope = rec(data)
      const groupsRaw = Array.isArray(envelope.groups) ? envelope.groups : null
      if (groupsRaw) {
        return catalogFromGroups(
          groupsRaw.map((item) => {
            const g = rec(item)
            return {
              key: str(g.key),
              title: str(g.title),
              description: str(g.description),
              sort_order: num(g.sort_order ?? g.sortOrder, 0),
              permissions: asList(g.permissions as unknown[] | null).map((p) => {
                const row = rec(p)
                return {
                  id: str(row.id),
                  code: str(row.code),
                  title: str(row.title) || undefined,
                  description: str(row.description),
                  module: str(row.module) || undefined,
                } satisfies ApiPermission
              }),
            }
          }),
        )
      }
      const rows = asList(data as ApiPermission[] | { items?: ApiPermission[]; permissions?: ApiPermission[] } | null)
      return catalogFromPermissions(
        rows.map((row) => {
          const item = rec(row)
          return {
            id: str(item.id),
            code: str(item.code),
            title: str(item.title) || undefined,
            description: str(item.description),
            module: str(item.module) || undefined,
          }
        }),
      )
    },
    staleTime: 5 * 60_000,
  })
}

function useCatalog() {
  const query = usePermissionCatalog()
  return { ...query, catalog: query.data ?? emptyPermissionCatalog() }
}

export function useAccessRoles() {
  const { catalog, isFetched } = useCatalog()
  return useQuery({
    queryKey: keys.roles,
    queryFn: fetchRoleRows,
    enabled: isFetched,
    select: (data) => data.map((row) => mapRole(row, catalog)),
  })
}

export function useAccessRole(id: string) {
  const { catalog, isFetched } = useCatalog()
  return useQuery({
    queryKey: keys.role(id),
    enabled: !!id && isFetched,
    queryFn: () => api.get<unknown>(`/api/v1/roles/${id}`),
    select: (data) => mapRole(data, catalog),
  })
}

/** Rol/izin ekranlarındaki kullanıcı eşlemeleri için hafif model. */
export function useAccessUsers() {
  return useQuery({ queryKey: keys.users, queryFn: fetchUserRows, staleTime: 60_000, select: (data) => data.map(mapUser) })
}

/** Kullanıcı yönetimi tablosu (tam liste; arama ve sayfalama istemcide). */
export function useDirectoryUsers() {
  return useQuery({ queryKey: keys.users, queryFn: fetchUserRows, staleTime: 60_000, select: (data) => data.map(mapDirectoryUser) })
}

export function useRoleOptions() {
  return useQuery({
    queryKey: keys.roles,
    queryFn: fetchRoleRows,
    staleTime: 60_000,
    select: (data) =>
      data.map((row) => {
        const item = rec(row)
        return { id: str(item.id), name: str(item.name) }
      }),
  })
}

export function useAccessProjects() {
  return useQuery({
    queryKey: keys.projects,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<AccessProject[]> =>
      rows(await api.get<unknown>('/api/v1/projects')).map((row) => {
        const item = rec(row)
        return { id: str(item.id), name: str(item.name) || str(item.code) }
      }),
  })
}

export function useDepartments() {
  return useQuery({
    queryKey: keys.departments,
    queryFn: async () => parseDepartments(await api.get<unknown>('/api/v1/hr/departments/list-all')),
    staleTime: 60_000,
  })
}

/** Aktif personel dizini (`list-all`) — yönetici ve bağlanabilir personel seçicileri aynı önbelleği paylaşır. */
function useEmployeeDirectory<T>(enabled: boolean, select: (rows: unknown[]) => T) {
  return useQuery({
    queryKey: hrKeys.employees,
    enabled,
    staleTime: 60_000,
    queryFn: fetchEmployeeDirectory,
    select,
  })
}

export function useManagerOptions(enabled = true) {
  return useEmployeeDirectory(enabled, (data): AccessUser[] => data.map(mapEmployeeDetails))
}

/** Aktif, work_email dolu, henüz kullanıcı bağlı olmayan personeller (bağlama seçici). */
export function useLinkableEmployees(enabled = true) {
  return useEmployeeDirectory(enabled, (data): LinkableEmployee[] =>
    data.map(mapLinkableEmployee).filter((e) => e.isActive && !!e.workEmail.trim() && !e.hasUser),
  )
}

export function useMailProviders(enabled = true) {
  return useQuery({
    queryKey: keys.mailProviders,
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<MailProviderOption[]> => {
      const data = await api.get<unknown>('/api/v1/mail/providers')
      return asList(data as { providers?: unknown[]; items?: unknown[] } | unknown[] | null).map((item) => {
        const row = rec(item)
        return {
          id: str(row.id),
          name: str(row.name ?? row.display_name ?? row.label),
          kind: str(row.kind ?? row.type ?? row.provider),
          isDefault: Boolean(row.is_default ?? row.isDefault),
        }
      }).filter((p) => p.id)
    },
  })
}

export function useCreateMailProvider() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; kind: string; isDefault?: boolean }) =>
      api.post('/api/v1/mail/providers', {
        name: input.name.trim(),
        kind: input.kind,
        is_default: Boolean(input.isDefault),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.mailProviders })
      void qc.invalidateQueries({ queryKey: ['admin'] })
    },
  })
}

export function useDirectoryUser(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.user(id),
    enabled: enabled && !!id,
    queryFn: async (): Promise<DirectoryUserRow> => mapDirectoryUser(await api.get<unknown>(`/api/v1/users/${id}`)),
  })
}

function positionBody(input: PositionInput) {
  return {
    department_id: input.departmentId,
    title: input.title.trim(),
    level: input.level,
    description: input.description.trim(),
    ...(input.confirmDepartmentChange ? { confirm_department_change: true } : {}),
  }
}

/** Pozisyonların tamamı (`list-all`); departman filtresi ve arama istemcide. */
export function usePositions() {
  return useQuery({
    queryKey: keys.positions,
    staleTime: 60_000,
    queryFn: async () => parsePositions(await api.get<unknown>('/api/v1/hr/positions/list-all')),
  })
}

export function useSavePosition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: PositionInput }) =>
      id ? api.put(`/api/v1/hr/positions/${id}`, positionBody(input)) : api.post('/api/v1/hr/positions', positionBody(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.positions }),
  })
}

export function useDeletePosition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm: boolean }) =>
      api.del(`/api/v1/hr/positions/${id}?confirm_delete=${confirm ? 'true' : 'false'}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.positions }),
  })
}

/** Rol veri kapsamı: departman/proje kapsamı hedef ister; diğerleri hedefsizdir (BE de doğrular). */
function scopeBody(input: RoleInput) {
  const needsTargets = input.scope === 'department' || input.scope === 'project'
  return { scope: input.scope, scope_targets: needsTargets ? [...new Set(input.scopeTargets.filter(Boolean))] : [] }
}

export function useSaveRole() {
  const qc = useQueryClient()
  const { catalog } = useCatalog()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: RoleInput }) => {
      const permission_ids = toPermissionIds(input.permissions, catalog)
      const confirm = Boolean(input.confirmUserReassignment)
      let roleId: string
      if (id) {
        const body: Record<string, unknown> = {
          name: input.name.trim(),
          description: input.description.trim(),
          ...scopeBody(input),
        }
        if (input.userIds !== undefined) {
          body.user_ids = input.userIds
          body.confirm_user_reassignment = confirm
        }
        await api.put(`/api/v1/roles/${id}`, body)
        await api.put(`/api/v1/roles/${id}/permissions`, { permission_ids })
        roleId = id
      } else {
        const saved = await api.post<unknown>('/api/v1/roles', {
          name: input.name.trim(),
          description: input.description.trim(),
          permission_ids,
          user_ids: input.userIds ?? [],
          confirm_user_reassignment: confirm,
          ...scopeBody(input),
        })
        roleId = str(rec(saved).id)
      }
      return mapRole(await api.get<unknown>(`/api/v1/roles/${roleId}`), catalog)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.roles })
      void qc.invalidateQueries({ queryKey: keys.users })
    },
  })
}

export function useRoleActivity(id: string, page = 1, action = '') {
  return useQuery({
    queryKey: keys.roleActivity(id, page, action),
    enabled: !!id,
    queryFn: async (): Promise<RoleActivityPage> => {
      const data = await api.get<unknown>(`/api/v1/roles/${id}/activity`, {
        page,
        limit: 20,
        action: action || undefined,
      })
      const envelope = rec(data)
      const items = asList((envelope.items as unknown[]) ?? data)
        .map(mapRoleActivityEntry)
        .filter((e): e is RoleActivityEntry => !!e)
      return {
        items,
        total: pageTotal(data, items.length),
        page: num(envelope.page, page),
        limit: num(envelope.limit, 20),
      }
    },
  })
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient()
  const { catalog } = useCatalog()
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      api.put(`/api/v1/roles/${id}/permissions`, { permission_ids: toPermissionIds(permissions, catalog) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.roles }),
  })
}

export function useSetRoleStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: RecordStatus }) => {
      const cached = qc.getQueryData<unknown[]>(keys.roles)?.find((row) => str(rec(row).id) === id)
      const current = mapRole(cached ?? (await api.get(`/api/v1/roles/${id}`)), emptyPermissionCatalog())
      await api.put(`/api/v1/roles/${id}`, {
        name: current.name,
        description: current.description,
        is_active: status === 'active',
      })
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.roles }),
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/roles/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.roles }),
  })
}

export function useAssignRoleUsers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      userIds,
      confirmUserReassignment = false,
    }: {
      id: string
      userIds: string[]
      confirmUserReassignment?: boolean
    }) => {
      await api.put(`/api/v1/roles/${id}/users`, {
        user_ids: userIds,
        confirm_user_reassignment: confirmUserReassignment,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.roles })
      void qc.invalidateQueries({ queryKey: keys.users })
    },
  })
}

export function useRemoveRoleUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, userId, remainingUserIds }: { id: string; userId: string; remainingUserIds?: string[] }) => {
      const next =
        remainingUserIds ??
        strList(rec(qc.getQueryData<unknown[]>(keys.roles)?.find((row) => str(rec(row).id) === id)).user_ids).filter(
          (uid) => uid !== userId,
        )
      await api.put(`/api/v1/roles/${id}/users`, {
        user_ids: next,
        confirm_user_reassignment: false,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.roles })
      void qc.invalidateQueries({ queryKey: keys.users })
    },
  })
}

export function useSaveDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: DepartmentInput }) => {
      const data = id
        ? await api.put<unknown>(`/api/v1/hr/departments/${id}`, departmentBody(input))
        : await api.post<unknown>('/api/v1/hr/departments', departmentBody(input))
      return mapDepartment(data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.departments })
      void qc.invalidateQueries({ queryKey: hrKeys.employees, exact: true })
    },
  })
}

export function useDeleteDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/hr/departments/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.departments }),
  })
}

export function useCreateAccessUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: DirectoryUserInput) => {
      const body: Record<string, unknown> = {
        employee_id: input.employeeId,
        role_ids: input.roleIds,
        password: input.password?.trim() ?? '',
      }
      const email = input.email?.trim()
      const first = input.firstName?.trim()
      const last = input.lastName?.trim()
      if (email) body.email = email
      if (first) body.first_name = first
      if (last) body.last_name = last
      if (input.mailbox?.providerId && input.mailbox.secret) {
        body.mailbox = mailboxBody(input.mailbox)
      }
      return api.post('/api/v1/users', body)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.users })
      void qc.invalidateQueries({ queryKey: hrKeys.employees, exact: true })
    },
  })
}

export function useCreateMailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MailAccountInput) =>
      api.post('/api/v1/mail/accounts', {
        user_id: input.userId,
        provider_id: input.providerId,
        email: input.email.trim(),
        secret: input.secret,
        ...(input.signatureHtml?.trim() ? { signature_html: input.signatureHtml.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.users })
      void qc.invalidateQueries({ queryKey: ['admin'] })
    },
  })
}

export function useUpdateAccessUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      firstName,
      lastName,
      roleIds,
      mailbox,
    }: {
      id: string
      firstName: string
      lastName: string
      roleIds?: string[]
      mailbox?: MailboxUpdateInput
    }) => {
      const body: Record<string, unknown> = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }
      if (mailbox) body.mailbox = mailboxUpdateBody(mailbox)
      await api.put(`/api/v1/users/${id}`, body)
      if (roleIds) await api.put(`/api/v1/users/${id}/roles`, { role_ids: roleIds })
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keys.users })
      void qc.invalidateQueries({ queryKey: keys.user(vars.id) })
    },
  })
}

export function useSetUserActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/api/v1/users/${id}/${active ? 'activate' : 'deactivate'}`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  })
}

export function useResetUserPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/users/${id}/reset-password`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  })
}
